import { api } from '../../../core/network/api';
import { DEFAULT_CUSTOM_LOADOUT } from '../../../../../shared/src/arsenal';
import { weaponById } from '../../../../../shared/src/weapons';
import { formatDate, t } from '../../i18n';
import { renderAsync } from '../async-view';
import {
  badge,
  badgeArt,
  type BadgeArt,
  button,
  card,
  icon,
  type IconName,
  progressBar,
  sectionHeading,
  statTile,
  tabStrip,
} from '../ui';
import { countUp, revealOnMount } from '../fx';
import { skinStillPath, stillImg, weaponStillPath } from './renders';
import { playerCardPlate } from '../player-card-plate';
import { NO_CARD } from '../../../../../shared/src/player-cards';
import type { RouteMount } from './types';


/**
 * The rank medal worn on the player card, by level band — presentation only, the level number
 * itself is always the server's. Ten levels a tier up to the top art the shell ships.
 */
const LEVEL_MEDALS: readonly BadgeArt[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];
function medalForLevel(level: number): BadgeArt {
  return LEVEL_MEDALS[Math.min(LEVEL_MEDALS.length - 1, Math.floor(Math.max(0, level - 1) / 10))]!;
}

/**
 * The Career route — the reference's PROFILE screen: character render on the left, stats on
 * the right behind STATS / MATCH HISTORY tabs, all-time leaderboard-rank plates, and the
 * (currently fixed) loadout along the bottom.
 *
 * Every number is server truth from existing endpoints:
 *   - `GET /api/match/profile` — level, xp, rankPoints, career counters, pre-computed ratios,
 *     recent matches. `ratios` is only ever formatted here, never recomputed (two divisions
 *     disagreeing about a zero denominator is a bug nobody could reproduce).
 *   - `GET /api/cosmetics` — the equipped skin id, so the left panel shows the figure the
 *     arena actually renders (same still set the shop uses).
 *   - `GET /api/leaderboard/:category/me` — the three rank plates. All-time ranks, labelled
 *     that way: the game has no weekly boards, and a "WEEK" label would be a fabrication.
 *
 *   - `GET /api/game/achievements` — the medals tab. Sprint 08 P8-I; before it existed this
 *     file's header said the reference's ACHIEVEMENTS tab was "omitted rather than shipped
 *     empty", which was the right call then and is why the tab still hides itself when the
 *     call fails.
 */
/** `GET /api/game/achievements` — see `backend/src/game/achievements/achievements.types.ts`. */
interface AchievementEntryDto {
  slug: string;
  name: string;
  description: string;
  target: number;
  art: BadgeArt;
  progress: number;
  unlocked: boolean;
  unlockedAt: string | null;
}

interface AchievementCatalogueDto {
  entries: AchievementEntryDto[];
  unlockedCount: number;
  total: number;
}

interface ArenaCareer {
  matches: number;
  wins: number;
  losses: number;
  kills: number;
  deaths: number;
  headshots: number;
  hits: number;
  shots: number;
}

interface ArenaRatios {
  killsPerDeath: number;
  winRate: number;
  accuracy: number;
  headshotRate: number;
}

interface RecentMatch {
  mapName: string;
  endedAt: string;
  kills: number;
  deaths: number;
  won: boolean;
  secondsPlayed: number;
  xpAwarded: number;
}

interface ArenaProfile {
  userId: string;
  username: string | null;
  level: number;
  xp: number;
  rankPoints: number;
  career: ArenaCareer;
  ratios: ArenaRatios;
  recent: RecentMatch[];
}

interface PersonalRank {
  rank: number;
  value: number;
  total: number;
}

interface CareerData {
  profile: ArenaProfile;
  equippedSkin: number;
  /** Display name of the worn title, or null for none. Resolved here rather than stored on the
   * profile because a title is meta-layer content the arena never sees — see
   * `backend/src/game/titles/titles.types.ts`. */
  equippedTitle: string | null;
  /** The worn player card, or `NO_CARD` for Standard Issue. Meta-layer content the arena never
   * sees — the one cosmetic category with no wire presence at all, by design. */
  equippedCard: number;
  /** The medals, or null when that call failed — the tab hides itself rather than erroring. */
  achievements: AchievementCatalogueDto | null;
  /** The ACTIVE player-built loadout `GET /api/game/loadout` reports (loadouts.md §4) — the
   * kit the next ticket carries. Name, weapon ids by slot, and the finish on each. */
  activeLoadout: { name: string; slots: readonly number[]; skins: readonly number[] };
  ranks: { label: string; rank: PersonalRank | null }[];
}

/** The three rank circles — categories registered by `arena-stats.service.ts`. */
const RANK_CIRCLES: { key: string; labelKey: string }[] = [
  { key: 'arena_kills', labelKey: 'career.kills' },
  { key: 'arena_wins', labelKey: 'career.wins' },
  { key: 'rank_points', labelKey: 'career.rank' },
];

/**
 * XP within the current level. `XP_PER_LEVEL` is a backend env (default 100) that no endpoint
 * exposes, so this bar assumes the default — presentation only: the level NUMBER is always the
 * server's, and a reconfigured curve would only stretch the bar, never lie about the level.
 */
const XP_PER_LEVEL = 100;

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/**
 * One all-time leaderboard placing, as a painted plate: the tier chevron, the placing large
 * beside it and the board's name under it.
 */
function rankPlate(label: string, rank: PersonalRank | null, index: number): HTMLElement {
  // `basis-full` below `sm`: three plates sharing a 375px row left each about 110px, and the
  // caption — the half that says WHICH board this rank is on — truncated to "KIL…", "WI…",
  // "RA…". A rank with no board is a number that means nothing.
  const wrap = document.createElement('div');
  wrap.className =
    'kit-plate [--kp-rim:12px] [--kp-pad:12px_14px] kit-deal flex items-center gap-3 min-w-0 basis-full sm:basis-0 sm:flex-1';
  wrap.style.setProperty('--i', String(index));
  wrap.append(icon('rank', { size: 40 }));
  const text = document.createElement('div');
  text.className = 'min-w-0';
  const value = document.createElement('div');
  value.className = 'kit-label font-heading text-[26px] leading-none tabular-nums';
  value.textContent = rank ? `#${rank.rank}` : '—';
  const caption = document.createElement('div');
  caption.className = 'text-[10px] font-heading uppercase tracking-[0.14em] text-text-dim mt-1';
  caption.textContent = t('career.allTime', { label });
  text.append(value, caption);
  wrap.append(text);
  return wrap;
}

/** A figure in a painted capsule with its icon first. */
function figureChip(art: IconName, text: string): HTMLElement {
  const el = document.createElement('span');
  el.className = 'kit-chip [--kh:30px] kit-label font-heading text-[12px] tabular-nums';
  el.append(icon(art, { size: 20 }));
  const value = document.createElement('span');
  value.textContent = text;
  el.append(value);
  return el;
}

/** One recent match: a recess cut into the window with its win/loss icon, the map and date, and
 * the figures as chips. */
function historyRow(match: RecentMatch, index: number): HTMLElement {
  const row = document.createElement('div');
  row.className = 'kit-well kit-deal flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5';
  row.style.setProperty('--i', String(Math.min(index, 12)));
  row.append(icon(match.won ? 'trophy' : 'close', { size: 36 }));

  const left = document.createElement('div');
  left.className = 'min-w-0 grow basis-[120px]';
  const map = document.createElement('div');
  map.className = 'kit-label font-heading uppercase text-sm truncate';
  map.textContent = match.mapName;
  const when = document.createElement('div');
  when.className = 'text-[11px] text-text-dim';
  // `formatDate`, never `toLocaleString(undefined, …)`: the browser's locale is the machine's,
  // not the player's choice of interface language — a match on an English UI read "15 сент.,
  // 15:06" on a Russian-configured machine (2026-09-18).
  when.textContent = formatDate(match.endedAt, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  left.append(map, when);

  row.append(
    left,
    figureChip('kills', `${match.kills} / ${match.deaths}`),
    figureChip('xp', `+${match.xpAwarded}`),
    badge(match.won ? t('career.win') : t('career.loss'), match.won ? 'success' : 'error'),
  );
  return row;
}

export const mountCareerRoute: RouteMount = (outlet, ctx) => {
  const content = document.createElement('div');
  outlet.append(content);

  const dispose = renderAsync<CareerData>(content, {
    load: async () => {
      // The rank plates must never take the whole screen down — a leaderboard hiccup is a
      // missing circle, not a broken career page. The profile fetch is the load-bearing one.
      const [profile, cosmetics, titles, achievements, playerCard, equippedLoadout, ...rankResults] =
        await Promise.all([
        api.get<ArenaProfile>('/match/profile'),
        api.get<{ skins: Array<{ id: number; equipped: boolean }> }>('/cosmetics').catch(() => null),
        // Same treatment as the cosmetics call and the rank circles: swallowed, because a
        // missing title is a missing line and not a broken career page.
        api
          .get<{ titles: Array<{ slug: string; name: string; equipped: boolean }> }>('/game/titles')
          .catch(() => null),
        // Swallowed like the two above: a career page whose medals failed to load is a career
        // page with one fewer tab, not an error screen.
        api.get<AchievementCatalogueDto>('/game/achievements').catch(() => null),
        // The worn player card. Swallowed like the calls around it: a career page whose card
        // failed to load is a career page with a plain name row, not an error screen.
        api.get<{ equippedCard: number }>('/player-cards').catch(() => null),
        // The loadout the player has actually equipped. Swallowed like the three above, and
        // for the same reason. Before this, the strip at the bottom of this screen iterated
        // `DEFAULT_LOADOUT` and labelled it "(default)" — so a player who had equipped
        // Marksman on the Loadout screen was told by their own profile that they carry an AR.
        // Two screens in the same product contradicting each other about a fact one of them
        // wrote.
        api
          .get<{ loadouts: Array<{ name: string; slots: number[]; skins: number[]; active: boolean }> }>('/game/loadout')
          .catch(() => null),
        ...RANK_CIRCLES.map((c) =>
          api.get<PersonalRank>(`/leaderboard/${c.key}/me`).catch(() => null),
        ),
      ]);
      return {
        profile,
        equippedSkin: cosmetics?.skins?.find((s) => s.equipped)?.id ?? 0,
        equippedTitle: titles?.titles?.find((row) => row.equipped)?.name ?? null,
        equippedCard: playerCard?.equippedCard ?? NO_CARD,
        achievements,
        // Falls back to the default kit only when the call failed — which is the same thing the
        // server hands a player who has never built anything (it creates `Default` on first read).
        activeLoadout:
          equippedLoadout?.loadouts?.find((row) => row.active) ??
          equippedLoadout?.loadouts?.[0] ??
          DEFAULT_CUSTOM_LOADOUT,
        ranks: RANK_CIRCLES.map((c, i) => ({ label: t(c.labelKey), rank: rankResults[i] ?? null })),
      };
    },
    // No `isEmpty` branch. It used to replace this entire screen — level, XP bar, rank
    // circles, the whole career table, the medal wall and the match history — with a single
    // icon and one sentence the moment `matches === 0`, which is every account that has not
    // finished a match yet. That is the state a new player is in on the screen where they are
    // deciding whether this game has anything in it, and what they got was an apology.
    //
    // The screen's own shape IS the answer: a career table of zeros, an XP bar at the start of
    // level 1, a wall of medals nobody has earned yet. Every one of those reads as "here is
    // what there is to get" where a blank panel reads as "there is nothing here". So the real
    // view always renders, and the first-run case gets a callout at the top of it instead —
    // the same one call to action, over the thing it is talking about rather than in place of
    // it. `renderContent` was already zero-safe: the ratios are server-computed, `badgeArt`
    // draws an unearned medal dimmed by design, and the history pane has always had its own
    // inline empty line.
    renderContent(data, container) {
      container.innerHTML = '';
      const { profile } = data;

      // ── heading + STATS / MATCH HISTORY tabs in the trailing slot ──────────────────────
      const statsPane = document.createElement('div');
      const historyPane = document.createElement('div');
      historyPane.className = 'hidden';

      // Achievements live here rather than behind a tenth nav entry: they are permanent
      // milestones, which is the same thing this screen is already about, and the nav is full.
      const medalsPane = document.createElement('div');
      medalsPane.className = 'hidden';

      const tabDefinitions = [
        { id: 'stats', label: t('career.stats') },
        { id: 'history', label: t('career.matchHistory') },
        ...(data.achievements
          ? [
              {
                id: 'medals',
                label: t('career.medalsTab', {
                  unlocked: data.achievements.unlockedCount,
                  total: data.achievements.total,
                }),
              },
            ]
          : []),
      ];
      // Filled in as each pane's real content is built below — the tab switch itself only
      // knows which pane became visible, not what is inside it, so the reveal targets are
      // recorded once and replayed on every switch (and once more, on first paint).
      const revealTargets: Record<'stats' | 'history' | 'medals', HTMLElement[]> = {
        stats: [],
        history: [],
        medals: [],
      };
      function revealPane(id: string): void {
        for (const el of revealTargets[id as keyof typeof revealTargets] ?? []) {
          revealOnMount(el, { staggerMs: 30 });
        }
      }
      const tabs = tabStrip(tabDefinitions, 'stats', (id) => {
        tabs.setActive(id);
        statsPane.classList.toggle('hidden', id !== 'stats');
        historyPane.classList.toggle('hidden', id !== 'history');
        medalsPane.classList.toggle('hidden', id !== 'medals');
        revealPane(id);
      });
      container.append(sectionHeading(t('career.title'), { trailing: tabs.el }));

      // First run: one line over the real screen, saying what starts filling it in. Not a
      // replacement for the screen — see the note on `renderContent` above.
      if (profile.career.matches === 0) {
        const callout = document.createElement('div');
        callout.dataset.career = 'first-run';
        callout.className = 'kit-plate kit-pop [--kp-pad:14px_16px] flex flex-wrap items-center justify-between gap-3 mb-4';
        const copy = document.createElement('p');
        copy.className = 'flex items-center gap-3 text-[13px] text-text min-w-0';
        copy.prepend(icon('star', { size: 36, className: 'kit-float' }));
        copy.textContent = t('career.emptyStats');
        callout.append(copy, button(t('career.goToPlay'), { variant: 'primary', onClick: () => ctx.navigate('/play') }));
        container.append(callout);
      }

      // ── two-panel layout: figure left, panes right ──────────────────────────────────────
      const columns = document.createElement('div');
      columns.className = 'grid gap-4 lg:grid-cols-[minmax(260px,340px)_1fr] items-start';

      // Left: the equipped figure, identity, level progress.
      const identity = document.createElement('div');
      identity.className = 'flex flex-col gap-2.5';
      const nameRow = document.createElement('div');
      // `break-words`, not `truncate`: a name is the one string on this screen that belongs to
      // the player, and cutting it is worse than letting it take a second line.
      nameRow.className = 'kit-title font-heading uppercase text-xl break-words min-w-0';
      nameRow.textContent = profile.username ?? ctx.user.username ?? ctx.user.email;

      // The title, under the name — the one place a title is worn. Omitted entirely rather than
      // rendered blank when there is none: an empty row reads as a loading state.
      const titleRow = document.createElement('div');
      if (data.equippedTitle) {
        titleRow.className = 'kit-label font-heading uppercase text-[12px] truncate';
        titleRow.textContent = data.equippedTitle;
        titleRow.dataset.career = 'title';
      }

      // The rank medal beside the level, then the XP groove and the figures in chips.
      const levelRow = document.createElement('div');
      levelRow.className = 'flex items-center gap-3';
      const levelText = document.createElement('span');
      levelText.className = 'kit-title font-heading uppercase text-[18px]';
      levelText.textContent = t('common.levelN', { level: profile.level });
      levelRow.append(badgeArt(medalForLevel(profile.level), { size: 64, className: 'w-14 h-14 kit-pop' }), levelText);
      const xpInLevel = profile.xp % XP_PER_LEVEL;
      const xpBar = progressBar({
        value: xpInLevel,
        max: XP_PER_LEVEL,
        label: t('career.xpToNext'),
        accent: 'cyan',
      });
      const chips = document.createElement('div');
      chips.className = 'flex flex-wrap gap-2';
      chips.append(
        figureChip('xp', `${xpInLevel}/${XP_PER_LEVEL}`),
        figureChip('rank', t('career.rankPoints', { points: profile.rankPoints })),
      );
      // The name sits ON the card, which is the whole product: a card is bought to be the
      // thing behind a name. Standard Issue returns the name row unadorned, so an account that
      // has bought none looks exactly as it did before.
      identity.append(playerCardPlate(data.equippedCard, nameRow), titleRow, levelRow, xpBar, chips);

      const figure = stillImg({
        src: skinStillPath(data.equippedSkin),
        alt: t('career.equippedCharacter'),
        width: 384,
        height: 512,
        className:
          'mx-auto h-[360px] w-auto object-contain object-bottom [filter:drop-shadow(0_10px_18px_rgba(0,0,0,0.6))]',
      });

      const leftPanel = card([identity, figure], { className: 'lg:sticky lg:top-4 kit-pop', title: t('career.title') });

      // ── Right / STATS: the 3x3 grid of career counters and server ratios. ─────────────────
      //
      // Nine plates, each with its own icon, centred figure at display size (`statTile` lg).
      const grid = document.createElement('div');
      grid.className = 'grid grid-cols-2 sm:grid-cols-3 gap-2.5';
      // Each cell carries its own numeric target and the formatter that reproduces the exact
      // static string above — `countUp` has to land on the same text `statTile` already drew,
      // or the animation's last frame would visibly differ from the value everyone already
      // saw once (motion off, a slow device, a re-render). Percentages count in whole points;
      // the ratio counts to two decimals; the rest are plain integers, no thousands separator,
      // matching `statTile`'s own `String(value)`.
      const intFormat = (v: number) => String(Math.round(v));
      const pctFormat = (v: number) => `${Math.round(v)}%`;
      const cells: { label: string; art: IconName; display: string | number; to: number; format: (v: number) => string }[] = [
        { label: t('career.gamesPlayed'), art: 'play', display: profile.career.matches, to: profile.career.matches, format: intFormat },
        { label: t('career.kills'), art: 'kills', display: profile.career.kills, to: profile.career.kills, format: intFormat },
        { label: t('career.deaths'), art: 'close', display: profile.career.deaths, to: profile.career.deaths, format: intFormat },
        {
          label: t('career.kd'), art: 'rank',
          display: profile.ratios.killsPerDeath.toFixed(2),
          to: profile.ratios.killsPerDeath,
          format: (v) => v.toFixed(2),
        },
        { label: t('career.winRate'), art: 'streak', display: pct(profile.ratios.winRate), to: profile.ratios.winRate * 100, format: pctFormat },
        { label: t('career.accuracy'), art: 'shield', display: pct(profile.ratios.accuracy), to: profile.ratios.accuracy * 100, format: pctFormat },
        {
          label: t('career.headshotRate'), art: 'star',
          display: pct(profile.ratios.headshotRate),
          to: profile.ratios.headshotRate * 100,
          format: pctFormat,
        },
        { label: t('career.headshots'), art: 'headshot', display: profile.career.headshots, to: profile.career.headshots, format: intFormat },
        { label: t('career.wins'), art: 'trophy', display: profile.career.wins, to: profile.career.wins, format: intFormat },
      ];
      cells.forEach((cell, index) => {
        const tile = statTile({ label: cell.label, art: cell.art, value: cell.display, size: 'lg', centred: true });
        tile.classList.add('kit-deal');
        tile.style.setProperty('--i', String(index));
        grid.append(tile);
        const valueEl = tile.querySelector<HTMLElement>('.tabular-nums');
        if (valueEl) countUp(valueEl, 0, cell.to, { format: cell.format });
      });
      statsPane.append(grid);
      revealTargets.stats.push(grid);

      // Rank plates — real personal ranks or nothing.
      const shownRanks = data.ranks.filter((r) => r.rank !== null);
      if (shownRanks.length > 0) {
        const plates = document.createElement('div');
        plates.className = 'flex flex-wrap gap-2.5 mt-2.5';
        shownRanks.forEach((r, index) => plates.append(rankPlate(r.label, r.rank, index)));
        statsPane.append(plates);
        revealTargets.stats.push(plates);
      }

      // Right / MATCH HISTORY.
      if (profile.recent.length === 0) {
        const p = document.createElement('p');
        p.className = 'kit-well px-3 py-3 text-sm text-text-dim';
        p.textContent = t('career.emptyHistory');
        historyPane.append(card([p]));
      } else {
        const list = document.createElement('div');
        list.className = 'flex flex-col gap-2';
        profile.recent.forEach((match, index) => list.append(historyRow(match, index)));
        historyPane.append(card([list], { padded: false }));
        revealTargets.history.push(list);
      }

      const right = document.createElement('div');
      right.className = 'min-w-0';
      // ── MEDALS: the achievements grid ──────────────────────────────────────────────────
      // A TROPHY CASE: each medal is mounted on its own kit plate, so it reads as an object on a
      // wall rather than a list icon; locked ones are dimmed and padlocked.
      if (data.achievements) {
        const grid = document.createElement('div');
        grid.className = 'grid gap-3 sm:grid-cols-2';
        data.achievements.entries.forEach((entry, index) => {
          const medal = badgeArt(entry.art, {
            size: 64,
            earned: entry.unlocked,
            className: 'w-12 h-12 sm:w-14 sm:h-14',
          });
          // The medal sits on its own plate; a locked one stays dimmed and wears the padlock.
          const mount = document.createElement('div');
          mount.className = 'relative shrink-0';
          mount.append(medal);
          if (!entry.unlocked) {
            mount.append(icon('locked', { size: 22, className: 'absolute -right-1 -bottom-1 z-[1]' }));
          }

          const text = document.createElement('div');
          text.className = 'flex-1 min-w-0';

          const nameRow = document.createElement('div');
          nameRow.className = 'flex items-center justify-between gap-2';
          const medalName = document.createElement('span');
          medalName.className = 'kit-label font-heading uppercase text-sm';
          medalName.textContent = entry.name;
          nameRow.append(medalName);
          // The date is the trophy: "earned 12 Aug" is what makes a milestone a memory rather
          // than a row. Locked entries get their target instead, which is the next thing to do.
          const dateOrTarget = document.createElement('span');
          dateOrTarget.className = 'text-[11px] font-heading uppercase tracking-wide text-text-dim shrink-0';
          dateOrTarget.textContent = entry.unlocked
            ? formatDate(entry.unlockedAt ?? Date.now(), { dateStyle: 'short' })
            : `${entry.progress} / ${entry.target}`;
          nameRow.append(dateOrTarget);

          const description = document.createElement('p');
          description.className = 'text-[12px] text-text-dim mt-0.5 mb-2';
          description.textContent = entry.description;

          text.append(nameRow, description);
          // A locked medal shows how far off it is; an earned one has nothing left to count.
          if (!entry.unlocked) {
            text.append(progressBar({ value: entry.progress, max: entry.target, label: entry.name }));
          }

          const tile = document.createElement('div');
          tile.className = [
            'kit-plate [--kp-rim:14px] [--kp-pad:12px] kit-deal flex items-start gap-3 min-w-0',
            entry.unlocked ? 'kit-plate-hit' : 'brightness-75',
          ].join(' ');
          tile.style.setProperty('--i', String(Math.min(index, 14)));
          tile.append(mount, text);
          grid.append(tile);
        });
        medalsPane.append(grid);
        revealTargets.medals.push(grid);
      }

      right.append(statsPane, historyPane, medalsPane);
      columns.append(leftPanel, right);
      container.append(columns);
      // First paint: the Stats pane is already the active tab, so its reveal has to run once
      // without waiting for a tab click — the switch handler above only fires on a later click.
      requestAnimationFrame(() => revealPane('stats'));

      // ── bottom strip: the active loadout — what every match launches with. One still per
      // weapon, stock finish: since 2026-09-10 there is no per-finish PNG to point at, and a
      // 56px-tall thumbnail was never where a player read their camouflage anyway. ──
      const loadoutStrip = document.createElement('div');
      loadoutStrip.className = 'grid grid-cols-3 gap-2.5';
      const wornLoadout = data.activeLoadout;
      wornLoadout.slots.forEach((weaponId, index) => {
        const weapon = weaponById(weaponId);
        const cell = document.createElement('div');
        cell.className = 'kit-plate [--kp-rim:12px] [--kp-pad:10px] kit-deal flex flex-col items-center gap-1.5 min-w-0';
        cell.style.setProperty('--i', String(index));
        cell.append(
          stillImg({
            src: weaponStillPath(weaponId),
            alt: weapon.name,
            width: 512,
            height: 320,
            className: 'h-14 w-auto max-w-full object-contain',
          }),
        );
        const label = document.createElement('span');
        label.className = 'kit-label font-heading uppercase text-[11px] truncate max-w-full';
        label.textContent = `${index + 1} · ${weapon.name}`;
        cell.append(label);
        loadoutStrip.append(cell);
      });
      const loadoutHeader = document.createElement('div');
      loadoutHeader.className = 'kit-header [--kh:42px] mt-4 mb-2';
      loadoutHeader.append(icon('loadout', { size: 26 }));
      const loadoutTitle = document.createElement('span');
      loadoutTitle.className = 'kit-title font-heading uppercase text-[15px] truncate';
      loadoutTitle.textContent = t('career.loadoutNamed', { name: wornLoadout.name });
      loadoutHeader.append(loadoutTitle);
      container.append(loadoutHeader, loadoutStrip);
    },
  });

  return () => {
    dispose();
    content.remove();
  };
};
